// @ts-nocheck
/**
 * ドロップマネージャー
 *
 * 責務:
 * - ドロップテーブルからの抽選
 * - レアリティ補正
 * - 報酬の確定
 * - ドロップ履歴管理
 */

import {
  getFloorDropTable,
  getBossDropTable,
  getChestDropTable,
  getAllCategoryDropTables,
  getCardDropTable,
  EVENT_REWARD_TABLES,
  rollExpOrbBattleDrop,
  rollExpOrbChestDrop,
  rollExpOrbBossDrop,
  getExpOrbClearReward,
} from './dropTables';
import { rewardCalculator } from './rewardCalculator';

/**
 * ドロップマネージャークラス
 */
class DropManager {
  constructor() {
    // 直近のドロップ履歴（統計用）
    this.dropHistory = [];

    // セッション統計
    this.sessionStats = {
      totalExperience: 0,
      totalGold: 0,
      materialsCollected: {},
      raresFound: 0,
    };
  }

  /**
   * フロアクリア報酬を生成
   *
   * @param {Object} floor - フロア情報
   * @param {Object} context - コンテキスト（鍵、パーティ、大陸など）
   * @returns {Object[]} ドロップアイテム配列
   */
  generateFloorRewards(floor, context) {
    const drops = [];
    const table = getFloorDropTable(context.continent, floor.depth);

    // 基本ドロップ（経験値、ゴールド）
    const experience = rewardCalculator.calculateExperience(floor, context);
    const gold = rewardCalculator.calculateGold(floor, context);

    drops.push({ type: 'experience', value: experience });
    drops.push({ type: 'gold', value: gold });

    // 統計更新
    this.sessionStats.totalExperience += experience;
    this.sessionStats.totalGold += gold;

    // 素材ドロップ
    const materialDrops = this._rollMaterialDrops(table, context);
    drops.push(...materialDrops);

    // レア素材チャンス
    const luckModifier = context.luckModifier || 1.0;
    if (Math.random() < (table.rareChance || 0) * luckModifier) {
      const rareDrop = this._rollRareDrop(table, context);
      if (rareDrop) {
        drops.push(rareDrop);
        this.sessionStats.raresFound++;
      }
    }

    // ドロップ履歴に追加
    this._recordDrops(floor, drops);

    return drops;
  }

  /**
   * ボス撃破報酬を生成
   *
   * @param {Object} boss - ボス情報
   * @param {Object} context - コンテキスト
   * @returns {Object[]} ドロップアイテム配列
   */
  generateBossRewards(boss, context) {
    const drops = [];
    // cardId → category → attribute → default のフォールバック
    const table = getBossDropTable(boss.cardId || boss.id, {
      category: boss.category,
      attribute: boss.attribute,
    });

    // 確定報酬
    if (table.guaranteed) {
      for (const item of table.guaranteed) {
        drops.push({
          type: item.type,
          item: item.item,
          count: item.count,
          isGuaranteed: true,
        });
        this._addToMaterialStats(item.item, item.count);
      }
    }

    // ボーナス経験値・ゴールド
    const experience = rewardCalculator.calculateBossExperience(boss, context);
    const gold = rewardCalculator.calculateBossGold(boss, context);

    drops.push({ type: 'experience', value: experience });
    drops.push({ type: 'gold', value: gold });

    this.sessionStats.totalExperience += experience;
    this.sessionStats.totalGold += gold;

    // レアドロップ
    const luckModifier = context.luckModifier || 1.0;
    for (const rareDrop of table.rareDrops || []) {
      if (Math.random() < rareDrop.chance * luckModifier) {
        drops.push({
          type: rareDrop.type,
          item: rareDrop.item,
          count: rareDrop.count,
          isRare: true,
        });
        this._addToMaterialStats(rareDrop.item, rareDrop.count);
        this.sessionStats.raresFound++;
      }
    }

    // 初回クリアボーナス
    if (context.isFirstClear && table.firstClearBonus) {
      for (const bonus of table.firstClearBonus) {
        drops.push({
          type: bonus.type,
          item: bonus.item,
          count: bonus.count,
          isFirstClear: true,
        });
        if (bonus.type === 'material') {
          this._addToMaterialStats(bonus.item, bonus.count);
        }
      }
    }

    return drops;
  }

  /**
   * 宝箱報酬を生成
   *
   * @param {string} chestType - 宝箱タイプ ('common' | 'rare' | 'epic' | 'legendary')
   * @param {Object} context - コンテキスト
   * @returns {Object[]} ドロップアイテム配列
   */
  generateChestRewards(chestType, context) {
    const table = getChestDropTable(chestType);
    if (!table) return [];

    const drops = [];
    const rollCount = table.rollCount || 1;

    for (let i = 0; i < rollCount; i++) {
      const drop = this._rollFromWeightedTable(table.items);
      if (drop) {
        const count = this._rollCount(drop.countRange);

        if (drop.type === 'gold') {
          drops.push({ type: 'gold', value: count });
          this.sessionStats.totalGold += count;
        } else {
          drops.push({
            type: drop.type,
            item: drop.item,
            count,
          });
          if (drop.type === 'material') {
            this._addToMaterialStats(drop.item, count);
          }
        }
      }
    }

    return drops;
  }

  /**
   * イベント報酬を生成
   *
   * @param {string} eventType - イベントタイプ
   * @param {Object} context - コンテキスト
   * @param {boolean} success - イベント成功フラグ（祭壇など）
   * @returns {Object[]} ドロップアイテム配列
   */
  generateEventRewards(eventType, context, success = true) {
    const eventTable = EVENT_REWARD_TABLES[eventType];
    if (!eventTable) return [];

    const drops = [];

    // 祭壇など成否が関係するイベント
    if (eventTable.type === 'altar') {
      const rewards = success ? eventTable.successRewards : eventTable.failureRewards;
      for (const reward of rewards || []) {
        drops.push({
          type: reward.type,
          item: reward.item,
          count: reward.count,
          duration: reward.duration,
        });
        if (reward.type === 'material') {
          this._addToMaterialStats(reward.item, reward.count);
        }
      }
      return drops;
    }

    // 宝箱発見イベント
    if (eventTable.type === 'treasure') {
      for (const reward of eventTable.rewards) {
        if (Math.random() < reward.chance) {
          const chestDrops = this.generateChestRewards(reward.chestType, context);
          drops.push(...chestDrops);
          break; // 最初にヒットした宝箱のみ
        }
      }
      return drops;
    }

    // 通常の報酬
    for (const reward of eventTable.rewards || []) {
      if (reward.type === 'chest') {
        if (Math.random() < reward.chance) {
          const chestDrops = this.generateChestRewards(reward.chestType, context);
          drops.push(...chestDrops);
        }
      } else {
        drops.push({
          type: reward.type,
          item: reward.item,
          count: reward.count,
        });
        if (reward.type === 'material') {
          this._addToMaterialStats(reward.item, reward.count);
        }
      }
    }

    return drops;
  }

  /**
   * 戦闘勝利時のドロップを生成
   *
   * @param {Object} params - パラメータ
   * @param {string} params.continentId - 大陸ID
   * @param {number} params.depthRatio - 深度割合 (0-1)
   * @param {string} params.battleType - 戦闘タイプ ('normal' | 'elite' | 'boss')
   * @param {Object[]} params.enemies - 敵情報配列
   * @returns {Object[]} ドロップアイテム配列 [{ type, count }]
   */
  generateBattleDrops({ continentId, depthRatio, battleType, enemies = [] }) {
    const drops = [];

    // 大陸ごとの基本素材タイプ（continents.jsの定義に準拠）
    const continentMaterials = {
      aviculus: ['魔力の核', '炎の結晶', '光の結晶'],       // 巨鳥の島（炎・光）
      black_curse: ['魔力の核', '闇の結晶', '水の結晶'],    // 黒呪の大陸（闇・水）
      steam_boundary: ['魔力の核', '炎の結晶', '水の結晶'], // 蒸気の境界（炎・水）
      twilight_realm: ['魔力の核', '光の結晶', '闇の結晶'], // 黄昏の境界（光・闘）
      time_rift: ['魔力の核', '未来の結晶'],                // 時空の狭間（未来）
      primal_depths: ['魔力の核', '原始の結晶'],            // 原始の深淵（原始）
      unknown: ['魔力の核'],                                // 未知の領域
    };

    const materialPool = continentMaterials[continentId] || ['魔力の核'];

    // 基本ドロップ確率（深度と戦闘タイプで変動）
    const baseChance = 0.3 + (depthRatio * 0.3);
    const typeMultiplier = battleType === 'boss' ? 2.5 : battleType === 'elite' ? 1.5 : 1.0;

    // 各敵からのドロップ
    const enemyCount = Math.max(1, enemies.length);
    for (let i = 0; i < enemyCount; i++) {
      if (Math.random() < baseChance * typeMultiplier) {
        const materialType = materialPool[Math.floor(Math.random() * materialPool.length)];
        const count = Math.floor(1 + Math.random() * 2 * typeMultiplier);
        drops.push({ type: materialType, count });
        this._addToMaterialStats(materialType, count);
      }
    }

    // ボス戦闘の確定ドロップ
    if (battleType === 'boss') {
      const rareMaterial = materialPool[0] + '_rare';
      drops.push({ type: rareMaterial, count: 1 });
      this._addToMaterialStats(rareMaterial, 1);
      this.sessionStats.raresFound++;

      // ボス戦の経験値オーブドロップ（高確率）
      const floor = Math.max(1, Math.floor(depthRatio * 100));
      const bossOrbDrops = rollExpOrbBossDrop(floor);
      if (bossOrbDrops && bossOrbDrops.length > 0) {
        for (const bossOrb of bossOrbDrops) {
          drops.push({ type: bossOrb.orbId, count: bossOrb.count });
          this._addToMaterialStats(bossOrb.orbId, bossOrb.count);
        }
      }
    } else {
      // 通常戦闘の経験値オーブドロップ（低確率）
      const floor = Math.max(1, Math.floor(depthRatio * 100));
      const orbDrop = rollExpOrbBattleDrop(floor);
      if (orbDrop) {
        drops.push({ type: orbDrop.orbId, count: orbDrop.count });
        this._addToMaterialStats(orbDrop.orbId, orbDrop.count);
      }
    }

    return drops;
  }

  /**
   * ハイブリッド敵ドロップを生成
   * 優先順位: カードID固有 > カテゴリ > 大陸
   *
   * @param {Object} params - パラメータ
   * @param {Object} params.enemy - 敵データ（cardId, category, attribute を含む）
   * @param {string} params.continentId - 大陸ID
   * @param {number} params.depthRatio - 深度割合 (0-1)
   * @param {string} params.battleType - 戦闘タイプ ('normal' | 'elite' | 'boss')
   * @param {number} [params.luckModifier=1.0] - 運補正
   * @returns {Object[]} ドロップアイテム配列 [{ type, item, count, source }]
   */
  generateEnemyDrops({ enemy, continentId, depthRatio, battleType, luckModifier = 1.0 }) {
    const drops = [];

    if (!enemy) {
      // 敵情報がない場合は従来の大陸ベースドロップ
      return this.generateBattleDrops({ continentId, depthRatio, battleType, enemies: [] });
    }

    const { cardId, category } = enemy;

    // 1. カードID固有ドロップをチェック
    const cardTable = getCardDropTable(cardId);
    if (cardTable) {
      const cardDrops = this._processDropTable(cardTable, depthRatio, luckModifier, 'card');
      drops.push(...cardDrops);
    }

    // 2. カテゴリドロップをチェック（複数カテゴリの場合は全て処理）
    const categoryTables = getAllCategoryDropTables(category);
    for (const { category: catName, table: categoryTable } of categoryTables) {
      const categoryDrops = this._processDropTable(categoryTable, depthRatio, luckModifier, `category:${catName}`);
      drops.push(...categoryDrops);
    }

    // 3. カードID・カテゴリどちらもない場合は大陸ドロップ
    if (!cardTable && categoryTables.length === 0) {
      const floorTable = getFloorDropTable(continentId, Math.floor(depthRatio * 100));
      if (floorTable) {
        const floorDrops = this._processDropTable(floorTable, depthRatio, luckModifier, 'continent');
        drops.push(...floorDrops);
      }
    }

    // 4. ボス戦の追加報酬
    if (battleType === 'boss') {
      // ボス専用テーブルを取得（cardId → category → attribute → default のフォールバック）
      const { category: enemyCategory, attribute: enemyAttribute } = enemy;
      const bossTable = getBossDropTable(cardId, {
        category: enemyCategory,
        attribute: enemyAttribute,
      });
      if (bossTable && bossTable.guaranteed) {
        for (const item of bossTable.guaranteed) {
          drops.push({
            type: item.type || 'material',
            item: item.item,
            count: item.count,
            source: 'boss_guaranteed',
            isGuaranteed: true,
          });
          this._addToMaterialStats(item.item, item.count);
        }
      }

      // ボス戦の経験値オーブドロップ（高確率）
      const floor = Math.max(1, Math.floor(depthRatio * 100));
      const bossOrbDrops = rollExpOrbBossDrop(floor);
      if (bossOrbDrops && bossOrbDrops.length > 0) {
        for (const bossOrb of bossOrbDrops) {
          drops.push({
            type: 'exp_orb',
            item: bossOrb.orbId,
            count: bossOrb.count,
            source: 'boss',
          });
          this._addToMaterialStats(bossOrb.orbId, bossOrb.count);
        }
      }
    } else {
      // 通常戦闘の経験値オーブドロップ（低確率）
      const floor = Math.max(1, Math.floor(depthRatio * 100));
      const orbDrop = rollExpOrbBattleDrop(floor);
      if (orbDrop) {
        drops.push({
          type: 'exp_orb',
          item: orbDrop.orbId,
          count: orbDrop.count,
          source: 'battle',
        });
        this._addToMaterialStats(orbDrop.orbId, orbDrop.count);
      }
    }

    return drops;
  }

  /**
   * ドロップテーブルを処理
   *
   * @param {Object} table - ドロップテーブル
   * @param {number} depthRatio - 深度割合
   * @param {number} luckModifier - 運補正
   * @param {string} source - ドロップソース識別子
   * @returns {Object[]} ドロップ配列
   * @private
   */
  _processDropTable(table, depthRatio, luckModifier, source) {
    const drops = [];
    const depthBonus = 1 + depthRatio * 0.5;

    // 確定ドロップ
    if (table.guaranteed) {
      for (const item of table.guaranteed) {
        drops.push({
          type: 'material',
          item: item.item,
          count: item.count,
          source,
          isGuaranteed: true,
        });
        this._addToMaterialStats(item.item, item.count);
      }
    }

    // 通常素材ドロップ
    if (table.materials) {
      for (const material of table.materials) {
        if (Math.random() < material.chance * luckModifier) {
          const baseCount = this._rollCount(material.countRange);
          const count = Math.max(1, Math.floor(baseCount * depthBonus));
          drops.push({
            type: 'material',
            item: material.item,
            count,
            source,
          });
          this._addToMaterialStats(material.item, count);
        }
      }
    }

    // レアドロップ
    if (table.rareChance && table.rarePool) {
      if (Math.random() < table.rareChance * luckModifier) {
        const rareDrop = this._rollFromWeightedTable(
          table.rarePool.map(item => ({ ...item, type: 'rare_material' }))
        );
        if (rareDrop) {
          drops.push({
            type: 'rare_material',
            item: rareDrop.item,
            count: 1,
            source,
            isRare: true,
          });
          this._addToMaterialStats(rareDrop.item, 1);
          this.sessionStats.raresFound++;
        }
      }
    }

    return drops;
  }

  /**
   * 複数敵のハイブリッドドロップを生成
   *
   * @param {Object} params - パラメータ
   * @param {Object[]} params.enemies - 敵データ配列
   * @param {string} params.continentId - 大陸ID
   * @param {number} params.depthRatio - 深度割合 (0-1)
   * @param {string} params.battleType - 戦闘タイプ
   * @param {number} [params.luckModifier=1.0] - 運補正
   * @returns {Object[]} ドロップアイテム配列
   */
  generateMultiEnemyDrops({ enemies, continentId, depthRatio, battleType, luckModifier = 1.0 }) {
    const allDrops = [];

    for (const enemy of enemies) {
      const enemyDrops = this.generateEnemyDrops({
        enemy,
        continentId,
        depthRatio,
        battleType: enemy.isBoss ? 'boss' : (enemy.isStrong ? 'elite' : battleType),
        luckModifier,
      });
      allDrops.push(...enemyDrops);
    }

    // ドロップをマージ（同じアイテムは合算）
    return this._mergeDrops(allDrops);
  }

  /**
   * ドロップをマージ（同じアイテムは合算）
   *
   * @param {Object[]} drops - ドロップ配列
   * @returns {Object[]} マージされたドロップ配列
   * @private
   */
  _mergeDrops(drops) {
    const merged = {};

    for (const drop of drops) {
      const key = `${drop.type}_${drop.item}`;
      if (merged[key]) {
        merged[key].count += drop.count;
      } else {
        merged[key] = { ...drop };
      }
    }

    return Object.values(merged);
  }

  /**
   * 宝箱イベントからのドロップを生成
   *
   * @param {Object} params - パラメータ
   * @param {string} params.continentId - 大陸ID
   * @param {number} params.depthRatio - 深度割合 (0-1)
   * @param {string} params.treasureRarity - 宝箱レアリティ ('common' | 'rare' | 'epic' | 'legendary')
   * @returns {Object[]} ドロップアイテム配列 [{ type, count }]
   */
  generateTreasureDrops({ continentId, depthRatio, treasureRarity = 'common' }) {
    const drops = [];

    // 大陸ごとの宝箱素材（continents.jsの定義に準拠）
    const treasureMaterials = {
      aviculus: ['魔力の核', '炎の結晶', '光の結晶'],       // 巨鳥の島（炎・光）
      black_curse: ['魔力の核', '闇の結晶', '水の結晶'],    // 黒呪の大陸（闇・水）
      steam_boundary: ['魔力の核', '炎の結晶', '水の結晶'], // 蒸気の境界（炎・水）
      twilight_realm: ['魔力の核', '光の結晶', '闇の結晶'], // 黄昏の境界（光・闇）
      time_rift: ['魔力の核', '未来の結晶'],                // 時空の狭間（未来）
      primal_depths: ['魔力の核', '原始の結晶'],            // 原始の深淵（原始）
      unknown: ['魔力の核'],                                // 未知の領域
    };

    const materialPool = treasureMaterials[continentId] || ['魔力の核'];

    // レアリティによるドロップ数と品質
    const rarityConfig = {
      common: { dropCount: 1, qualityMultiplier: 1.0, rareChance: 0.05 },
      rare: { dropCount: 2, qualityMultiplier: 1.5, rareChance: 0.15 },
      epic: { dropCount: 3, qualityMultiplier: 2.0, rareChance: 0.30 },
      legendary: { dropCount: 4, qualityMultiplier: 3.0, rareChance: 0.50 },
    };

    const config = rarityConfig[treasureRarity] || rarityConfig.common;
    const depthBonus = 1 + depthRatio * 0.5;

    // 通常ドロップ
    for (let i = 0; i < config.dropCount; i++) {
      const materialType = materialPool[Math.floor(Math.random() * materialPool.length)];
      const count = Math.floor((1 + Math.random() * 2) * config.qualityMultiplier * depthBonus);
      drops.push({ type: materialType, count });
      this._addToMaterialStats(materialType, count);
    }

    // レアドロップ判定
    if (Math.random() < config.rareChance) {
      const rareMaterialNames = {
        common: '秘宝の欠片',
        rare: '古代の秘宝',
        epic: '伝説の秘宝',
        legendary: '神話の秘宝',
      };
      const rareMaterial = rareMaterialNames[treasureRarity] || '秘宝の欠片';
      drops.push({ type: rareMaterial, count: 1 });
      this._addToMaterialStats(rareMaterial, 1);
      this.sessionStats.raresFound++;
    }

    // 宝箱からの経験値オーブドロップ
    const floor = Math.max(1, Math.floor(depthRatio * 100));
    const orbDrop = rollExpOrbChestDrop(floor, treasureRarity);
    if (orbDrop) {
      drops.push({ type: orbDrop.orbId, count: orbDrop.count });
      this._addToMaterialStats(orbDrop.orbId, orbDrop.count);
    }

    return drops;
  }

  /**
   * ダンジョンクリア報酬を生成
   *
   * @param {Object} dungeon - ダンジョン情報
   * @param {Object} context - コンテキスト
   * @returns {Object} クリア報酬
   */
  generateDungeonClearRewards(dungeon, context) {
    const baseRewards = rewardCalculator.calculateDungeonClearRewards(dungeon, context);

    const drops = [
      { type: 'experience', value: baseRewards.experience },
      { type: 'gold', value: baseRewards.gold },
    ];

    this.sessionStats.totalExperience += baseRewards.experience;
    this.sessionStats.totalGold += baseRewards.gold;

    // ダンジョン固有報酬
    if (dungeon.clearRewards) {
      for (const reward of dungeon.clearRewards) {
        drops.push({
          type: reward.type,
          item: reward.item,
          count: reward.count,
        });
        if (reward.type === 'material') {
          this._addToMaterialStats(reward.item, reward.count);
        }
      }
    }

    // ダンジョンクリア時の経験値オーブ報酬（確定）
    const maxFloor = dungeon.maxFloor || 10;
    const clearOrbs = getExpOrbClearReward(maxFloor);
    if (clearOrbs && clearOrbs.length > 0) {
      for (const reward of clearOrbs) {
        drops.push({
          type: 'exp_orb',
          item: reward.orbId,
          count: reward.count,
          isClearReward: true,
        });
        this._addToMaterialStats(reward.orbId, reward.count);
      }
    }

    return {
      drops,
      bonus: baseRewards.bonus,
      sessionStats: { ...this.sessionStats },
    };
  }

  /**
   * ボスドロップを抽選 (useDrops用)
   *
   * @param {Object} context - { continentId, depth }
   * @param {Object} enemy - 敵情報
   * @returns {Object} { gold, items }
   */
  rollBossDrops(context, enemy) {
    const { continentId, depth } = context;
    const items = this.generateBattleDrops({
      continentId,
      depthRatio: depth,
      battleType: 'boss',
      enemies: [enemy],
    });

    const gold = Math.floor(200 + depth * 500 + Math.random() * 200);

    return { gold, items };
  }

  /**
   * フロアドロップを抽選 (useDrops用)
   *
   * @param {Object} context - { continentId, depth }
   * @param {number} floor - 階層
   * @param {number} multiplier - 報酬倍率
   * @returns {Object} { gold, items }
   */
  rollFloorDrops(context, floor, multiplier = 1.0) {
    const { continentId, depth } = context;
    const items = this.generateBattleDrops({
      continentId,
      depthRatio: depth,
      battleType: multiplier > 1 ? 'elite' : 'normal',
      enemies: [],
    });

    const gold = Math.floor((50 + depth * 150) * multiplier);

    return { gold, items };
  }

  /**
   * 宝箱ドロップを抽選 (useDrops用)
   *
   * @param {Object} context - { continentId, depth }
   * @param {string} chestType - 宝箱タイプ
   * @returns {Object} { gold, items }
   */
  rollChestDrops(context, chestType) {
    const { continentId, depth } = context;

    // chestType を treasureRarity にマッピング
    const rarityMap = {
      normal: 'common',
      rare: 'rare',
      legendary: 'legendary',
      epic: 'epic',
    };
    const treasureRarity = rarityMap[chestType] || 'common';

    const items = this.generateTreasureDrops({
      continentId,
      depthRatio: depth,
      treasureRarity,
    });

    const goldMultiplier = { common: 1, rare: 2, epic: 3, legendary: 5 };
    const gold = Math.floor((100 + depth * 300) * (goldMultiplier[treasureRarity] || 1));

    return { gold, items };
  }

  /**
   * 素材ドロップを抽選
   * @private
   */
  _rollMaterialDrops(table, context) {
    const drops = [];
    const dropMultiplier = context.dropMultiplier || 1.0;

    for (const material of table.materials || []) {
      if (Math.random() < material.chance) {
        const baseCount = this._rollCount(material.countRange);
        const count = Math.floor(baseCount * dropMultiplier);

        if (count > 0) {
          drops.push({
            type: 'material',
            item: material.item,
            count,
          });
          this._addToMaterialStats(material.item, count);
        }
      }
    }

    return drops;
  }

  /**
   * レアドロップを抽選
   * @private
   */
  _rollRareDrop(table, context) {
    const rarePool = table.rarePool || [];
    if (rarePool.length === 0) return null;

    const drop = this._rollFromWeightedTable(
      rarePool.map(item => ({ ...item, type: 'rare_material' }))
    );

    if (drop) {
      const result = {
        type: 'rare_material',
        item: drop.item,
        count: 1,
        isRare: true,
      };
      this._addToMaterialStats(drop.item, 1);
      return result;
    }

    return null;
  }

  /**
   * 重み付きテーブルから抽選
   * @private
   */
  _rollFromWeightedTable(items) {
    if (!items || items.length === 0) return null;

    const totalWeight = items.reduce((sum, item) => sum + (item.weight || 1), 0);
    let roll = Math.random() * totalWeight;

    for (const item of items) {
      roll -= (item.weight || 1);
      if (roll <= 0) {
        return item;
      }
    }

    return items[items.length - 1];
  }

  /**
   * カウント範囲から抽選
   * @private
   */
  _rollCount(range) {
    if (!range) return 1;
    if (typeof range === 'number') return range;

    const [min, max] = range;
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  /**
   * 素材統計に追加
   * @private
   */
  _addToMaterialStats(item, count) {
    if (!item) return;
    this.sessionStats.materialsCollected[item] =
      (this.sessionStats.materialsCollected[item] || 0) + count;
  }

  /**
   * ドロップ履歴を記録
   * @private
   */
  _recordDrops(floor, drops) {
    this.dropHistory.push({
      floorId: floor.id,
      floorDepth: floor.depth,
      drops,
      timestamp: Date.now(),
    });

    // 履歴は直近100件のみ保持
    if (this.dropHistory.length > 100) {
      this.dropHistory.shift();
    }
  }

  /**
   * セッション統計をリセット
   */
  resetSessionStats() {
    this.sessionStats = {
      totalExperience: 0,
      totalGold: 0,
      materialsCollected: {},
      raresFound: 0,
    };
  }

  /**
   * セッション統計を取得
   */
  getSessionStats() {
    return { ...this.sessionStats };
  }

  /**
   * ドロップ履歴を取得
   */
  getDropHistory() {
    return [...this.dropHistory];
  }

  /**
   * ドロップ履歴をクリア
   */
  clearDropHistory() {
    this.dropHistory = [];
  }

  /**
   * ドロップをプレイヤーデータに適用
   *
   * @param {Object[]} drops - ドロップ配列
   * @param {Object} twinKeyData - プレイヤーのtwinKeyData
   * @returns {Object} 更新後のtwinKeyData
   */
  applyDropsToPlayerData(drops, twinKeyData) {
    const updated = { ...twinKeyData };

    for (const drop of drops) {
      switch (drop.type) {
        case 'material':
        case 'rare_material':
        case 'exp_orb':
          updated.materials = updated.materials || {};
          // exp_orbタイプはitemプロパティにオーブIDが入っている
          const materialKey = drop.item || drop.type;
          updated.materials[materialKey] =
            (updated.materials[materialKey] || 0) + drop.count;
          break;

        case 'equipment':
          updated.equipment = updated.equipment || [];
          updated.equipment.push({
            id: `eq_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            baseId: drop.item,
            level: 1,
            awakening: 0,
            equipped: null,
            obtainedAt: Date.now(),
          });
          break;

        case 'recipe':
          updated.unlockedRecipes = updated.unlockedRecipes || [];
          if (!updated.unlockedRecipes.includes(drop.item)) {
            updated.unlockedRecipes.push(drop.item);
          }
          break;

        case 'passive':
          updated.unlockedPassives = updated.unlockedPassives || [];
          if (!updated.unlockedPassives.includes(drop.item)) {
            updated.unlockedPassives.push(drop.item);
          }
          break;

        case 'title':
          updated.titles = updated.titles || [];
          if (!updated.titles.includes(drop.item)) {
            updated.titles.push(drop.item);
          }
          break;

        // experience, gold は別途処理（親コンポーネントで）
        default:
          break;
      }
    }

    return updated;
  }
}

export const dropManager = new DropManager();
export default DropManager;
